import axios, { AxiosError } from 'axios';

import { datadogRum } from 'root/datadog/datadogRum';

import { LS_INVOICING_API_ROOT } from '../../constants';
import { formatRefId } from '../../utils/referenceHelpers';
import { CustomerData } from './CreditAccountService';
import { getOrCreateCreditAccountId, putWithCreditAccountRetry } from './putWithCreditAccountRetry';

export interface ReturnInstructionPayload {
  amount: string;
  currency: string;
  applicationInstructions?: Array<{ transactionRef: string }>;
}

export interface ReturnInstructionResponse {
  id: string;
  amount: string;
  currency: string;
  transactionRef: string;
  creditAccountRef: string;
}

export interface UpsertReturnInstructionParams {
  /** The credit account ID from R-Series (can be empty/0 if customer doesn't have one yet) */
  creditAccountId: string | number;
  /** The customer ID from R-Series (required if creditAccountId is empty/0) */
  customerId: string;
  /** The sale/transaction ID */
  saleId: string;
  /** The return amount (should be positive - API expects positive amount) */
  amount: number;
  /** The currency code (e.g., 'USD', 'CAD') */
  currency: string;
  /** Customer data - required if credit account may not exist on Invoicing */
  customer?: CustomerData;
  /**
   * Original transaction references that this return should be applied against.
   * These are the sale IDs from the original transactions being returned.
   * If not provided or empty, only one unapplied credit memo will be created.
   */
  originalSaleIds?: Array<string | number>;
}

export default class ReturnInstructionService {
  /**
   * Creates or updates a return instruction for a transaction.
   * If the customer doesn't have a credit account on R-Series, it will create one with $0 limit.
   * If the credit account doesn't exist on Invoicing (404), it will create it and retry.
   *
   * @param params The parameters for upserting a return instruction
   * @returns The return instruction response with the credit account ID used
   */
  static async upsertReturnInstruction(
    params: UpsertReturnInstructionParams
  ): Promise<ReturnInstructionResponse & { creditAccountId: string }> {
    const { creditAccountId, customerId, saleId, amount, currency, customer, originalSaleIds } =
      params;

    if (!saleId) {
      throw new Error(`Missing required parameter: saleId=${saleId}`);
    }

    const creditAccountIdStr = await getOrCreateCreditAccountId(creditAccountId, customerId);
    const creditAccountRef = formatRefId(creditAccountIdStr);
    const transactionRef = formatRefId(`${saleId}`);

    // API expects amount as a string-encoded decimal (positive value)
    const payload: ReturnInstructionPayload = {
      amount: amount.toString(),
      currency,
    };

    // Add applicationInstructions if we have original sale IDs to link against
    if (originalSaleIds && originalSaleIds.length > 0) {
      payload.applicationInstructions = originalSaleIds.map((saleId) => ({
        transactionRef: formatRefId(`${saleId}`),
      }));
    }

    const url = `${LS_INVOICING_API_ROOT}/credit-accounts/${creditAccountRef}/transactions/${transactionRef}/return-instruction`;

    return putWithCreditAccountRetry<ReturnInstructionPayload, ReturnInstructionResponse>({
      url,
      payload,
      creditAccountIdStr,
      creditAccountRef,
      transactionRef,
      currency,
      customer,
      operationName: 'return instruction',
      datadogContext: { amount },
    });
  }

  /**
   * Deletes a return instruction for a transaction.
   * This is idempotent - returns successfully even if the instruction doesn't exist (404).
   *
   * @param creditAccountId The credit account ID from R-Series
   * @param saleId The sale/transaction ID
   */
  static async deleteReturnInstruction(
    creditAccountId: string | number,
    saleId: string
  ): Promise<void> {
    if (!creditAccountId || creditAccountId === 0 || creditAccountId === '0') {
      return;
    }

    if (!saleId) {
      return;
    }

    const creditAccountRef = formatRefId(`${creditAccountId}`);
    const transactionRef = formatRefId(`${saleId}`);

    const url = `${LS_INVOICING_API_ROOT}/credit-accounts/${creditAccountRef}/transactions/${transactionRef}/return-instruction`;

    try {
      await axios.delete(url);
    } catch (error) {
      const axiosError = error as AxiosError;

      if (axiosError.response?.status === 404) {
        return;
      }

      datadogRum.addError(error, {
        creditAccountRef,
        transactionRef,
        message: `Failed to delete return instruction for transactionRef: ${transactionRef}`,
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  }
}
